import moment from "moment-timezone";

// ---------------------------------------------------------------------------
// Open-Meteo response shapes (only the fields we request). The forecast is
// keyless/free; see https://open-meteo.com/. Times come back in the venue's
// local timezone because we request `timezone=auto`.
// ---------------------------------------------------------------------------
export interface OpenMeteoForecast {
  timezone: string;
  current?: {
    time: string;
    temperature_2m: number;
    apparent_temperature: number;
    relative_humidity_2m: number;
    precipitation: number;
    weather_code: number;
    wind_speed_10m: number;
    wind_gusts_10m: number;
  };
  hourly?: {
    time: string[];
    temperature_2m: number[];
    precipitation_probability: number[];
    weather_code: number[];
    wind_speed_10m: number[];
    wind_gusts_10m: number[];
    cape: number[];
    // Lightning Potential Index — only some high-res models provide it, so
    // entries can be null. Treated as optional everywhere downstream.
    lightning_potential?: (number | null)[];
  };
  daily?: {
    time: string[];
    weather_code: number[];
    temperature_2m_max: number[];
    temperature_2m_min: number[];
    precipitation_probability_max: number[];
  };
}

// NWS active-alerts (GeoJSON FeatureCollection). US-only, keyless.
// https://api.weather.gov/alerts/active?point=lat,lon
export interface NwsAlertProperties {
  event: string; // e.g. "Severe Thunderstorm Warning"
  severity: "Extreme" | "Severe" | "Moderate" | "Minor" | "Unknown";
  headline?: string;
  description?: string;
  ends?: string | null;
  expires?: string;
}
export interface NwsAlerts {
  features: { properties: NwsAlertProperties }[];
}

// ---------------------------------------------------------------------------
// WMO weather codes → label + emoji. https://open-meteo.com/en/docs (WMO table)
// ---------------------------------------------------------------------------
const WEATHER_CODES: Record<number, { label: string; icon: string }> = {
  0: { label: "Clear", icon: "☀️" },
  1: { label: "Mostly clear", icon: "🌤️" },
  2: { label: "Partly cloudy", icon: "⛅" },
  3: { label: "Overcast", icon: "☁️" },
  45: { label: "Fog", icon: "🌫️" },
  48: { label: "Rime fog", icon: "🌫️" },
  51: { label: "Light drizzle", icon: "🌦️" },
  53: { label: "Drizzle", icon: "🌦️" },
  55: { label: "Heavy drizzle", icon: "🌧️" },
  56: { label: "Freezing drizzle", icon: "🌧️" },
  57: { label: "Freezing drizzle", icon: "🌧️" },
  61: { label: "Light rain", icon: "🌦️" },
  63: { label: "Rain", icon: "🌧️" },
  65: { label: "Heavy rain", icon: "🌧️" },
  66: { label: "Freezing rain", icon: "🌧️" },
  67: { label: "Freezing rain", icon: "🌧️" },
  71: { label: "Light snow", icon: "🌨️" },
  73: { label: "Snow", icon: "🌨️" },
  75: { label: "Heavy snow", icon: "❄️" },
  77: { label: "Snow grains", icon: "🌨️" },
  80: { label: "Light showers", icon: "🌦️" },
  81: { label: "Showers", icon: "🌧️" },
  82: { label: "Violent showers", icon: "⛈️" },
  85: { label: "Snow showers", icon: "🌨️" },
  86: { label: "Snow showers", icon: "❄️" },
  95: { label: "Thunderstorm", icon: "⛈️" },
  96: { label: "Thunderstorm + hail", icon: "⛈️" },
  99: { label: "Severe thunderstorm", icon: "⛈️" },
};

export const describeWeatherCode = (code: number | undefined): { label: string; icon: string } =>
  (code != null && WEATHER_CODES[code]) || { label: "—", icon: "🌡️" };

/**
 * Find the hourly index that matches the event's local start hour. Open-Meteo
 * hourly times are venue-local strings ("2026-06-12T18:00"); we format the
 * event's start into the same shape using its own timezone so the comparison
 * is apples-to-apples regardless of the viewer's clock.
 */
export const findGameTimeIndex = (
  hourlyTimes: string[] | undefined,
  startISO: string,
  timezone: string
): number => {
  if (!hourlyTimes || hourlyTimes.length === 0) return -1;
  const target = moment.tz(startISO, timezone || "UTC").format("YYYY-MM-DDTHH:00");
  const exact = hourlyTimes.indexOf(target);
  if (exact !== -1) return exact;
  // Fall back to the nearest hour within the returned window.
  const targetMs = moment.tz(startISO, timezone || "UTC").valueOf();
  let best = -1;
  let bestDelta = Infinity;
  hourlyTimes.forEach((t, i) => {
    const delta = Math.abs(moment.tz(t, timezone || "UTC").valueOf() - targetMs);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = i;
    }
  });
  // Only trust the fallback if it's within ~6h of game time.
  return bestDelta <= 6 * 60 * 60 * 1000 ? best : -1;
};

// ---------------------------------------------------------------------------
// Thunderstorm / lightning risk. We don't have individual strike data on the
// free tier, so risk is derived from instability (CAPE), the Lightning
// Potential Index when available, precipitation probability, and any active
// NWS severe-thunderstorm / tornado alert — the actionable safety signal.
// ---------------------------------------------------------------------------
export type RiskLevel = "low" | "moderate" | "elevated" | "severe";

export interface StormRisk {
  level: RiskLevel;
  label: string;
  // Text color name understood by the Text atom.
  color: string;
  icon: string;
  drivers: string[];
}

const SEVERE_ALERT = /(tornado|severe thunderstorm) warning/i;
const WATCH_ALERT = /(tornado|severe thunderstorm) watch/i;

export interface RiskInputs {
  cape?: number; // J/kg
  lightningPotential?: number | null; // LPI
  precipProbability?: number; // %
  weatherCode?: number;
  alerts: NwsAlertProperties[];
}

export const computeStormRisk = (input: RiskInputs): StormRisk => {
  const cape = input.cape ?? 0;
  const lpi = input.lightningPotential ?? 0;
  const precip = input.precipProbability ?? 0;
  const isThunderCode = input.weatherCode != null && input.weatherCode >= 95;

  const drivers: string[] = [];
  if (cape > 0) drivers.push(`CAPE ${Math.round(cape)} J/kg`);
  if (lpi > 0) drivers.push(`LPI ${lpi.toFixed(1)}`);
  if (precip > 0) drivers.push(`${Math.round(precip)}% precip`);

  const severeAlert = input.alerts.find((a) => SEVERE_ALERT.test(a.event));
  if (severeAlert) {
    return {
      level: "severe",
      label: severeAlert.event,
      color: "dark-red",
      icon: "🌩️",
      drivers: [severeAlert.headline ?? severeAlert.event],
    };
  }

  const watchAlert = input.alerts.find((a) => WATCH_ALERT.test(a.event));

  if (watchAlert || isThunderCode || lpi > 2 || (cape >= 2000 && precip >= 40)) {
    return {
      level: "elevated",
      label: "Storm risk",
      color: "dark-orange",
      icon: "⛈️",
      drivers: watchAlert ? [watchAlert.event, ...drivers] : drivers,
    };
  }

  if (lpi > 0 || (cape >= 800 && precip >= 40) || precip >= 60) {
    return {
      level: "moderate",
      label: "Possible storms",
      color: "orange",
      icon: "🌦️",
      drivers,
    };
  }

  return {
    level: "low",
    label: "Low storm risk",
    color: "dark-green",
    icon: "✅",
    drivers,
  };
};
